// Per-medication dose adjustment for batch quick-log (MEDICATION_BATCH_DOSE_ADJUST_PLAN.md).
// Pure helpers shared by the web steppers and the server cap. Rounding stays in regimen.ts.

import { isFormUnit } from "./medication.js";
import {
  doseForPillCount,
  hasPillStepper,
  pillCountForDose,
  pillStep,
  roundPillCount,
  type RegimenItem,
} from "./regimen.js";

export type DoseControlKind = "pill" | "form" | "free" | "combo";

/** Subset of RegimenItem the controls need. A learned suggestion event is adapted to this shape. */
export type DoseControlItem = Pick<
  RegimenItem,
  "dose" | "unit" | "pillStrength" | "pillForm" | "pillHalfSteps" | "pillQuarterSteps" | "ingredients"
>;

/** Adjusted dose must stay within this multiple of the usual dose (D-7). */
export const MAX_DOSE_MULTIPLE = 10;

const FRACTION_GLYPHS: Record<number, string> = { 25: "¼", 50: "½", 75: "¾" };

/** combo if ingredients[]; pill if hasPillStepper; form if isFormUnit(unit); else free. */
export function doseControlKind(item: DoseControlItem): DoseControlKind {
  if (item.ingredients?.length) return "combo";
  if (hasPillStepper(item)) return "pill";
  if (isFormUnit(item.unit)) return "form";
  return "free";
}

/** pill/form: pillStep(item) (0.25|0.5|1). free: item.dose when > 0, else 1. combo: 0. */
export function stepFor(item: DoseControlItem): number {
  const kind = doseControlKind(item);
  if (kind === "combo") return 0;
  if (kind === "pill" || kind === "form") return pillStep(item);
  return typeof item.dose === "number" && item.dose > 0 ? item.dose : 1;
}

/** The saved default dose in item.unit (item.dose); for pill with no dose: pillStrength; else 1. */
export function usualDose(item: DoseControlItem): number {
  if (typeof item.dose === "number" && item.dose > 0) return item.dose;
  if (
    doseControlKind(item) === "pill" &&
    typeof item.pillStrength === "number" &&
    item.pillStrength > 0
  ) {
    return item.pillStrength;
  }
  return 1;
}

/** dose -> control amount. pill: pillCountForDose; form/free/combo: the dose itself. */
export function doseToAmount(item: DoseControlItem, dose: number): number {
  if (doseControlKind(item) === "pill") return pillCountForDose(item, dose);
  return dose;
}

/** control amount -> dose. pill: doseForPillCount (2 dp); form/free/combo: the amount. */
export function amountToDose(item: DoseControlItem, amount: number): number {
  if (doseControlKind(item) === "pill") return doseForPillCount(item, amount);
  return amount;
}

/**
 * Snap + bound an amount. pill/form: roundPillCount, then <= max.
 * free: > 0 and <= max (a non-positive amount floors at one step).
 * combo: the usual amount (the row is fixed).
 * max = MAX_DOSE_MULTIPLE * usual amount.
 */
export function clampAmount(item: DoseControlItem, amount: number): number {
  const kind = doseControlKind(item);
  const usualAmt = doseToAmount(item, usualDose(item));
  const max = MAX_DOSE_MULTIPLE * usualAmt;
  if (kind === "combo") return usualAmt;
  if (kind === "pill" || kind === "form") {
    const snapped = roundPillCount(item, amount);
    return snapped > max ? max : snapped;
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    const floor = stepFor(item);
    return floor > max ? max : floor;
  }
  return amount > max ? max : amount;
}

/** true when |dose - usualDose(item)| > 1e-9 (combo: always false). */
export function isAdjusted(item: DoseControlItem, dose: number): boolean {
  if (doseControlKind(item) === "combo") return false;
  return Math.abs(dose - usualDose(item)) > 1e-9;
}

/** "1½ tablets", "½ tablet", "1 capsule", "10 mL". Singular for amount <= 1. */
export function formatAmount(item: DoseControlItem, amount: number): string {
  const kind = doseControlKind(item);
  if (kind === "pill" || kind === "form") {
    const word = item.pillForm ?? (isFormUnit(item.unit) ? item.unit : "tablet");
    return `${formatQuantity(amount)} ${pluralForm(word, amount)}`;
  }
  const unit = item.unit ?? "";
  const qty = formatQuantity(amount);
  return unit ? `${qty} ${unit}` : qty;
}

/** Fractions only for .25/.5/.75; otherwise up to 2 decimal places. */
function formatQuantity(amount: number): string {
  const negative = amount < 0;
  const scaled = Math.round(Math.abs(amount) * 100) / 100;
  const whole = Math.floor(scaled + 1e-9);
  const hundredths = Math.round((scaled - whole) * 100);
  const glyph = FRACTION_GLYPHS[hundredths];
  let body: string;
  if (glyph) body = whole === 0 ? glyph : `${whole}${glyph}`;
  else if (hundredths === 0) body = String(whole);
  else body = String(scaled);
  return negative ? `-${body}` : body;
}

function pluralForm(word: string, amount: number): string {
  if (amount <= 1) return word;
  if (/(?:s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
}
